<template>
  <div class="vf">
    <header class="vf-head">
      <span class="vf-eyebrow">Clients</span>
      <h1 class="vf-title">Centre de vérification</h1>
      <p class="vf-sub">Contrôlez la pièce d'identité et le contrat signé de chaque client. Le client est notifié de chaque décision.</p>
    </header>

    <!-- ── Queues ─────────────────────────────────────────── -->
    <nav class="vf-queues" aria-label="Files de vérification">
      <button
        v-for="q in queues"
        :key="q.key"
        type="button"
        :class="['vf-queue', 'tone-' + q.tone, { 'is-active': filters.queue === q.key }]"
        :aria-pressed="filters.queue === q.key"
        @click="setQueue(q.key)"
      >
        <i :class="q.icon" aria-hidden="true"></i>
        <span>{{ q.label }}</span>
        <b>{{ counts[q.key] ?? 0 }}</b>
      </button>
    </nav>

    <section class="vf-card" aria-labelledby="vf-list-title">
      <div class="vf-list-head">
        <h2 id="vf-list-title">{{ currentQueue.label }} <span class="vf-count">{{ pager.total }}</span></h2>
        <div class="vf-search">
          <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
          <input
            v-model="filters.search"
            type="search"
            placeholder="Client, magasin, email, téléphone, CIN…"
            aria-label="Rechercher un client"
            @input="debouncedLoad"
          />
        </div>
      </div>

      <div v-if="loadError" class="vf-error" role="alert">
        <i class="ph ph-warning-circle" aria-hidden="true"></i>
        Impossible de charger les vérifications.
        <button type="button" class="btn btn-sm btn-light" @click="load()">Réessayer</button>
      </div>

      <template v-else>
        <div class="vf-table-wrap" :class="{ 'is-loading': loading }">
          <table class="vf-table">
            <thead>
              <tr>
                <th scope="col">Client</th>
                <th scope="col">Contact</th>
                <th scope="col">Pièce d'identité</th>
                <th scope="col">Contrat</th>
                <th scope="col">Soumis</th>
                <th scope="col"><span class="visually-hidden">Action</span></th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading && !rows.length">
                <tr v-for="n in 5" :key="'s' + n" class="vf-skel-row">
                  <td colspan="6"><span class="vf-skel"></span></td>
                </tr>
              </template>
              <tr
                v-for="r in rows"
                :key="r.id"
                :class="{ 'is-open': detail?.id === r.id }"
                tabindex="0"
                @click="openDetail(r.id)"
                @keydown.enter.prevent="openDetail(r.id)"
              >
                <td data-label="Client">
                  <div class="vf-client">
                    <img v-if="r.logo" :src="r.logo" alt="" class="vf-avatar" loading="lazy" />
                    <span v-else class="vf-avatar vf-avatar--empty">{{ initials(r.name) }}</span>
                    <div>
                      <strong>{{ r.store_name || r.name }}</strong>
                      <small>{{ r.store_name ? r.name + " · " : "" }}{{ r.ref }}</small>
                    </div>
                  </div>
                </td>
                <td data-label="Contact">
                  <span class="d-block">{{ r.phone || "—" }}</span>
                  <small class="vf-muted">{{ r.email }}</small>
                </td>
                <td data-label="Pièce d'identité">
                  <span :class="['vf-badge', 'tone-' + docState(r, 'cin').tone]">
                    <i :class="docState(r, 'cin').icon" aria-hidden="true"></i>{{ docState(r, "cin").label }}
                  </span>
                </td>
                <td data-label="Contrat">
                  <span :class="['vf-badge', 'tone-' + docState(r, 'contrat').tone]">
                    <i :class="docState(r, 'contrat').icon" aria-hidden="true"></i>{{ docState(r, "contrat").label }}
                  </span>
                </td>
                <td data-label="Soumis" class="text-nowrap">
                  <span class="d-block">{{ dateOnly(r.verification_time) }}</span>
                  <small class="vf-muted">{{ ago(r.verification_time) }}</small>
                </td>
                <td class="vf-cell-action">
                  <button type="button" class="vf-review-btn" @click.stop="openDetail(r.id)">
                    {{ needsReview(r) ? "Examiner" : "Voir" }}
                    <i class="ph ph-caret-right" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!loading && !rows.length" class="vf-empty">
            <i :class="filters.search ? 'ph ph-magnifying-glass' : 'ph ph-shield-check'" aria-hidden="true"></i>
            <p v-if="filters.search">Aucun client ne correspond à « {{ filters.search }} ».</p>
            <p v-else-if="filters.queue === 'pending'">Tout est à jour : aucun document en attente.</p>
            <p v-else>Aucun client dans cette file.</p>
          </div>
        </div>

        <footer v-if="pager.last_page > 1 || pager.total > 15" class="vf-pager">
          <span class="vf-muted">{{ pager.from || 0 }}–{{ pager.to || 0 }} sur {{ pager.total }}</span>
          <div class="vf-pager-ctrl">
            <select v-model.number="filters.per_page" class="form-select form-select-sm" aria-label="Lignes par page" @change="load(1)">
              <option v-for="n in [15, 30, 50]" :key="n" :value="n">{{ n }} / page</option>
            </select>
            <button type="button" class="vf-icon-btn" :disabled="pager.current_page <= 1" aria-label="Page précédente" @click="load(pager.current_page - 1)">
              <i class="ph ph-caret-left" aria-hidden="true"></i>
            </button>
            <span>{{ pager.current_page }} / {{ pager.last_page }}</span>
            <button type="button" class="vf-icon-btn" :disabled="pager.current_page >= pager.last_page" aria-label="Page suivante" @click="load(pager.current_page + 1)">
              <i class="ph ph-caret-right" aria-hidden="true"></i>
            </button>
          </div>
        </footer>
      </template>
    </section>

    <!-- ── Review drawer ──────────────────────────────────── -->
    <transition name="vf-fade"><div v-if="detail || detailLoading" class="vf-scrim" @click="closeDetail"></div></transition>
    <transition name="vf-slide">
      <aside
        v-if="detail || detailLoading"
        ref="drawerEl"
        class="vf-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vf-d-title"
        tabindex="-1"
        @keydown.esc="closeDetail"
      >
        <header class="vf-drawer-head">
          <div v-if="detail" class="vf-client">
            <img v-if="detail.logo" :src="detail.logo" alt="" class="vf-avatar vf-avatar--lg" />
            <span v-else class="vf-avatar vf-avatar--lg vf-avatar--empty">{{ initials(detail.name) }}</span>
            <div>
              <span class="vf-eyebrow">Vérification</span>
              <h2 id="vf-d-title">{{ detail.store_name || detail.name }}</h2>
              <small class="vf-muted">{{ detail.ref }} · soumis le {{ dateTime(detail.verification_time) }}</small>
            </div>
          </div>
          <h2 v-else id="vf-d-title" class="vf-muted">Chargement…</h2>
          <button type="button" class="vf-icon-btn" aria-label="Fermer" @click="closeDetail">
            <i class="ph ph-x" aria-hidden="true"></i>
          </button>
        </header>

        <div v-if="detailLoading && !detail" class="vf-drawer-body">
          <span v-for="n in 3" :key="n" class="vf-skel vf-skel--block"></span>
        </div>

        <div v-else-if="detail" class="vf-drawer-body">
          <!-- identity of the customer, to compare with the documents -->
          <dl class="vf-facts">
            <div><dt>Nom</dt><dd>{{ detail.name || "—" }}</dd></div>
            <div><dt>N° CIN déclaré</dt><dd class="vf-mono">{{ detail.cin || "—" }}</dd></div>
            <div><dt>Téléphone</dt><dd>{{ detail.phone || "—" }}</dd></div>
            <div><dt>Email</dt><dd>{{ detail.email || "—" }}</dd></div>
            <div class="vf-facts-wide"><dt>Adresse</dt><dd>{{ [detail.address, detail.city_name].filter(Boolean).join(", ") || "—" }}</dd></div>
          </dl>

          <section v-for="doc in docSections" :key="doc.key" class="vf-doc" :aria-labelledby="'vf-doc-' + doc.key">
            <div class="vf-doc-head">
              <h3 :id="'vf-doc-' + doc.key"><i :class="doc.icon" aria-hidden="true"></i>{{ doc.title }}</h3>
              <span :class="['vf-badge', 'tone-' + docState(detail, doc.key).tone]">
                <i :class="docState(detail, doc.key).icon" aria-hidden="true"></i>{{ docState(detail, doc.key).label }}
              </span>
            </div>

            <p v-if="!detail[doc.ask]" class="vf-muted vf-doc-none">Le client n'a pas encore envoyé ce document.</p>

            <template v-else>
              <div class="vf-files">
                <template v-for="f in doc.files" :key="f.field">
                  <a v-if="detail[f.field]" :href="detail[f.field]" target="_blank" rel="noopener" class="vf-file">
                    <img v-if="!isPdf(detail[f.field])" :src="detail[f.field]" :alt="f.label" loading="lazy" />
                    <span v-else class="vf-file-pdf"><i class="ph ph-file-pdf" aria-hidden="true"></i>PDF</span>
                    <span class="vf-file-cap">{{ f.label }} <i class="ph ph-arrow-square-out" aria-hidden="true"></i></span>
                  </a>
                  <div v-else class="vf-file vf-file--missing">
                    <span class="vf-file-pdf"><i class="ph ph-file-x" aria-hidden="true"></i>Fichier manquant</span>
                    <span class="vf-file-cap">{{ f.label }}</span>
                  </div>
                </template>
              </div>

              <p v-if="detail[doc.status] === 2 && detail[doc.reason]" class="vf-reason">
                <i class="ph ph-chat-circle-text" aria-hidden="true"></i>
                <span><b>Motif du refus :</b> {{ detail[doc.reason] }}</span>
              </p>

              <!-- decision -->
              <div v-if="detail[doc.status] === 0 || editing[doc.key]" class="vf-decide">
                <template v-if="rejecting[doc.key]">
                  <label :for="'vf-reason-' + doc.key" class="vf-label">Motif du refus <span class="text-danger">*</span></label>
                  <div class="vf-chips" role="group" aria-label="Motifs fréquents">
                    <button v-for="m in doc.reasons" :key="m" type="button" class="vf-chip" @click="reasons[doc.key] = m">{{ m }}</button>
                  </div>
                  <textarea
                    :id="'vf-reason-' + doc.key"
                    v-model="reasons[doc.key]"
                    class="form-control"
                    rows="3"
                    maxlength="500"
                    placeholder="Expliquez au client ce qu'il doit corriger"
                  ></textarea>
                  <div class="vf-decide-actions">
                    <button type="button" class="btn btn-light" :disabled="saving === doc.key" @click="rejecting[doc.key] = false">Annuler</button>
                    <button
                      type="button"
                      class="btn btn-danger"
                      :disabled="saving === doc.key || !reasons[doc.key]?.trim()"
                      @click="decide(doc.key, 2)"
                    >
                      <span v-if="saving === doc.key" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      Confirmer le refus
                    </button>
                  </div>
                </template>
                <div v-else class="vf-decide-actions">
                  <button v-if="editing[doc.key]" type="button" class="btn btn-light me-auto" @click="editing[doc.key] = false">Annuler</button>
                  <button type="button" class="btn btn-outline-danger" :disabled="!!saving" @click="startReject(doc.key)">
                    <i class="ph ph-x-circle" aria-hidden="true"></i> Refuser
                  </button>
                  <button
                    type="button"
                    class="btn btn-success"
                    :disabled="!!saving || !hasFiles(detail, doc)"
                    :title="hasFiles(detail, doc) ? '' : 'Fichier manquant : refusez pour demander un nouvel envoi'"
                    @click="decide(doc.key, 1)"
                  >
                    <span v-if="saving === doc.key" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <i v-else class="ph ph-check-circle" aria-hidden="true"></i> Approuver
                  </button>
                </div>
              </div>
              <button v-else type="button" class="vf-link" @click="editing[doc.key] = true">
                <i class="ph ph-pencil-simple" aria-hidden="true"></i> Modifier la décision
              </button>
            </template>
          </section>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";

type DocKey = "cin" | "contrat";
type Queue = "pending" | "rejected" | "verified" | "all";

interface VerificationRow {
  id: number;
  ref: string;
  name: string;
  store_name: string | null;
  logo: string | null;
  cin: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city_name: string | null;
  ask_cin_verified: number;
  cin_verified: number;
  raison_cin_verified: string | null;
  cin_recto: string | null;
  cin_verso: string | null;
  ask_cntr_verified: number;
  contrat_verified: number;
  raison_cntr_verified: string | null;
  contrat_file: string | null;
  verification_time: string | null;
  [key: string]: any;
}

const { showToast } = useAppToast();
const route = useRoute();
const router = useRouter();

const queues: { key: Queue; label: string; icon: string; tone: string }[] = [
  { key: "pending", label: "À examiner", icon: "ph ph-hourglass-medium", tone: "info" },
  { key: "rejected", label: "Refusés", icon: "ph ph-x-circle", tone: "danger" },
  { key: "verified", label: "Vérifiés", icon: "ph ph-seal-check", tone: "success" },
  { key: "all", label: "Tous", icon: "ph ph-list-bullets", tone: "neutral" },
];

const docSections = [
  {
    key: "cin" as DocKey,
    title: "Pièce d'identité (CIN)",
    icon: "ph ph-identification-card",
    ask: "ask_cin_verified",
    status: "cin_verified",
    reason: "raison_cin_verified",
    files: [
      { field: "cin_recto", label: "Recto" },
      { field: "cin_verso", label: "Verso" },
    ],
    reasons: ["Document illisible ou flou", "CIN expirée", "Le nom ne correspond pas au compte", "Recto ou verso manquant"],
  },
  {
    key: "contrat" as DocKey,
    title: "Contrat de service",
    icon: "ph ph-file-text",
    ask: "ask_cntr_verified",
    status: "contrat_verified",
    reason: "raison_cntr_verified",
    files: [{ field: "contrat_file", label: "Contrat signé" }],
    reasons: ["Contrat non signé", "Pages manquantes", "Document illisible", "Cachet de la société manquant"],
  },
];

const rows = ref<VerificationRow[]>([]);
const counts = ref<Partial<Record<Queue, number>>>({});
const loading = ref(false);
const loadError = ref(false);
const filters = reactive({ queue: "pending" as Queue, search: "", per_page: 15 });
const pager = reactive({ current_page: 1, last_page: 1, total: 0, from: 0, to: 0 });

const currentQueue = computed(() => queues.find((q) => q.key === filters.queue) || queues[0]);

let reqId = 0;
async function load(page = pager.current_page) {
  const id = ++reqId;
  loading.value = true;
  loadError.value = false;
  try {
    const { data } = await api.get("/api/auth/verifications", {
      params: { queue: filters.queue, searchTerm: filters.search.trim(), page, per_page: filters.per_page },
    });
    if (id !== reqId) return; // a newer request superseded this one
    rows.value = data.users.data;
    Object.assign(pager, {
      current_page: data.users.current_page,
      last_page: data.users.last_page,
      total: data.users.total,
      from: data.users.from,
      to: data.users.to,
    });
    counts.value = data.counts || {};
  } catch (e) {
    if (id === reqId) loadError.value = true;
  } finally {
    if (id === reqId) loading.value = false;
  }
}

let searchTimer: ReturnType<typeof setTimeout> | undefined;
function debouncedLoad() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => load(1), 350);
}

function setQueue(q: Queue) {
  if (filters.queue === q) return;
  filters.queue = q;
  load(1);
}

/* ── Document status ─────────────────────────────────────── */
function docState(r: VerificationRow, doc: DocKey) {
  const asked = doc === "cin" ? r.ask_cin_verified : r.ask_cntr_verified;
  const status = doc === "cin" ? r.cin_verified : r.contrat_verified;
  if (!Number(asked)) return { label: "Non envoyé", tone: "neutral", icon: "ph ph-minus-circle" };
  if (Number(status) === 1) return { label: "Approuvé", tone: "success", icon: "ph ph-check-circle" };
  if (Number(status) === 2) return { label: "Refusé", tone: "danger", icon: "ph ph-x-circle" };
  return { label: "À examiner", tone: "info", icon: "ph ph-hourglass-medium" };
}
const needsReview = (r: VerificationRow) =>
  (Number(r.ask_cin_verified) && Number(r.cin_verified) === 0) ||
  (Number(r.ask_cntr_verified) && Number(r.contrat_verified) === 0);
const hasFiles = (r: VerificationRow, doc: (typeof docSections)[number]) => doc.files.every((f) => !!r[f.field]);

/* ── Drawer ──────────────────────────────────────────────── */
const detail = ref<VerificationRow | null>(null);
const detailLoading = ref(false);
const drawerEl = ref<HTMLElement | null>(null);
const saving = ref<DocKey | "">("");
const rejecting = reactive<Record<DocKey, boolean>>({ cin: false, contrat: false });
const editing = reactive<Record<DocKey, boolean>>({ cin: false, contrat: false });
const reasons = reactive<Record<DocKey, string>>({ cin: "", contrat: "" });
let lastFocus: HTMLElement | null = null;

function resetDecision() {
  (["cin", "contrat"] as DocKey[]).forEach((k) => {
    rejecting[k] = false;
    editing[k] = false;
    reasons[k] = "";
  });
}

async function openDetail(id: number) {
  if (!detail.value && !detailLoading.value) lastFocus = document.activeElement as HTMLElement;
  resetDecision();
  detailLoading.value = true;
  try {
    const { data } = await api.get(`/api/auth/verifications/show/${id}`);
    detail.value = data.customer;
    if (route.query.open != String(id)) router.replace({ query: { ...route.query, open: String(id) } });
    await nextTick();
    drawerEl.value?.focus();
  } catch (e: any) {
    showToast(e?.response?.data?.message || "Impossible d'ouvrir ce dossier", "error");
    closeDetail();
  } finally {
    detailLoading.value = false;
  }
}

function closeDetail() {
  detail.value = null;
  detailLoading.value = false;
  if (route.query.open) {
    const { open, ...rest } = route.query;
    router.replace({ query: rest });
  }
  lastFocus?.focus?.();
}

function startReject(doc: DocKey) {
  rejecting[doc] = true;
  reasons[doc] = "";
  nextTick(() => document.getElementById("vf-reason-" + doc)?.focus());
}

async function decide(doc: DocKey, statut: 1 | 2) {
  if (!detail.value) return;
  const id = detail.value.id;
  saving.value = doc;
  try {
    const url = doc === "cin" ? `/api/auth/verifications/active-cin/${id}` : `/api/auth/verifications/active-contrat/${id}`;
    const { data } = await api.post(url, { statut, raison: statut === 2 ? reasons[doc].trim() : null });
    if (!data.success) throw { response: { data } };
    showToast(data.message, "success");
    rejecting[doc] = false;
    editing[doc] = false;
    // refresh the drawer and the list (the row may leave the current queue)
    const [{ data: fresh }] = await Promise.all([api.get(`/api/auth/verifications/show/${id}`), load()]);
    detail.value = fresh.customer;
  } catch (e: any) {
    const err = e?.response?.data;
    const first = err?.errors ? (Object.values(err.errors)[0] as string[])[0] : null;
    showToast(first || err?.message || "Erreur serveur, veuillez réessayer", "error");
  } finally {
    saving.value = "";
  }
}

/* ── Formatting ──────────────────────────────────────────── */
const isPdf = (url: string) => /\.pdf($|[?#])/i.test(url || "");
const initials = (name: string) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
const toDate = (v: string | null) => (v ? new Date(String(v).replace(" ", "T")) : null);
const dateOnly = (v: string | null) => toDate(v)?.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) || "—";
const dateTime = (v: string | null) =>
  toDate(v)?.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) || "—";
function ago(v: string | null) {
  const d = toDate(v);
  if (!d) return "";
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 60) return `il y a ${mins} min`;
  const h = Math.round(mins / 60);
  if (h < 24) return `il y a ${h} h`;
  const days = Math.round(h / 24);
  return days < 31 ? `il y a ${days} j` : `il y a ${Math.round(days / 30)} mois`;
}

onMounted(async () => {
  await load(1);
  if (route.query.open) openDetail(Number(route.query.open));
});
// the header alert links to ?open=<id>, also while this page is already displayed
watch(
  () => route.query.open,
  (open) => {
    if (open && Number(open) !== detail.value?.id && !detailLoading.value) openDetail(Number(open));
  }
);
onBeforeUnmount(() => clearTimeout(searchTimer));
</script>

<style scoped>
.vf {
  padding-bottom: var(--ui-space-6);
}
.vf-head {
  margin-bottom: var(--ui-space-5);
}
.vf-eyebrow {
  display: block;
  margin-bottom: 4px;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-eyebrow);
  text-transform: uppercase;
}
.vf-title {
  margin: 0;
  font-size: clamp(1.6rem, 2.6vw, 2.2rem);
  font-weight: var(--ui-fw-black);
  letter-spacing: -0.02em;
}
.vf-sub {
  max-width: 680px;
  margin: 6px 0 0;
  color: var(--ui-text-muted);
}
.vf-muted {
  color: var(--ui-text-muted);
}
.vf-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* Queues */
.vf-queues {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--ui-space-3);
  margin-bottom: var(--ui-space-4);
}
.vf-queue {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  color: var(--ui-text-secondary);
  font-weight: var(--ui-fw-bold);
  text-align: left;
  transition: border-color var(--ui-dur-fast) var(--ui-ease), box-shadow var(--ui-dur-fast) var(--ui-ease);
}
.vf-queue i {
  font-size: 20px;
}
.vf-queue b {
  margin-left: auto;
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
  color: var(--ui-text);
}
.vf-queue:hover {
  box-shadow: var(--ui-shadow-card);
}
.vf-queue:focus-visible {
  outline: none;
  box-shadow: var(--ui-focus-ring);
}
.vf-queue.is-active {
  border-color: var(--ui-accent);
  background: var(--ui-accent-soft);
  color: var(--ui-text);
}
.vf-queue.tone-info i { color: var(--ui-info); }
.vf-queue.tone-danger i { color: var(--ui-danger); }
.vf-queue.tone-success i { color: var(--ui-success); }
.vf-queue.tone-neutral i { color: var(--ui-text-muted); }

/* Card + list */
.vf-card {
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
  overflow: hidden;
}
.vf-list-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--ui-space-3);
  padding: var(--ui-space-4) var(--ui-space-5);
  border-bottom: 1px solid var(--ui-border);
}
.vf-list-head h2 {
  margin: 0;
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
}
.vf-count {
  margin-left: 6px;
  padding: 2px 9px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.vf-search {
  position: relative;
  width: 320px;
  max-width: 100%;
}
.vf-search i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ui-text-muted);
}
.vf-search input {
  width: 100%;
  height: 40px;
  padding: 0 14px 0 38px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
}
.vf-search input:focus {
  outline: none;
  border-color: var(--ui-accent);
  box-shadow: var(--ui-focus-ring);
}
.vf-error {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: var(--ui-space-5);
  padding: 12px 16px;
  border-radius: var(--ui-radius-md);
  background: var(--ui-danger-soft);
  color: var(--ui-danger-strong);
}
.vf-error .btn {
  margin-left: auto;
}

.vf-table-wrap {
  overflow-x: auto;
  transition: opacity var(--ui-dur-fast) var(--ui-ease);
}
.vf-table-wrap.is-loading {
  opacity: 0.6;
}
.vf-table {
  width: 100%;
  border-collapse: collapse;
}
.vf-table th {
  padding: 12px 16px;
  background: var(--ui-surface-sunken);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
  white-space: nowrap;
}
.vf-table td {
  padding: 14px 16px;
  border-top: 1px solid var(--ui-border);
  vertical-align: middle;
  font-size: var(--ui-fs-sm);
}
.vf-table tbody tr {
  cursor: pointer;
  transition: background-color var(--ui-dur-fast) var(--ui-ease);
}
.vf-table tbody tr:hover,
.vf-table tbody tr.is-open {
  background: var(--ui-surface-hover);
}
.vf-table tbody tr:focus-visible {
  outline: 2px solid var(--ui-accent);
  outline-offset: -2px;
}
.vf-table small {
  display: block;
}
.vf-cell-action {
  text-align: right;
}
.vf-client {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.vf-client strong {
  display: block;
  color: var(--ui-text);
}
.vf-client small {
  color: var(--ui-text-muted);
}
.vf-avatar {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  background: var(--ui-surface-sunken);
}
.vf-avatar--lg {
  width: 52px;
  height: 52px;
}
.vf-avatar--empty {
  display: grid;
  place-items: center;
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-black);
}
.vf-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: var(--ui-radius-pill);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  white-space: nowrap;
}
.vf-badge.tone-info { background: var(--ui-info-soft); color: var(--ui-info); }
.vf-badge.tone-success { background: var(--ui-success-soft); color: var(--ui-success); }
.vf-badge.tone-danger { background: var(--ui-danger-soft); color: var(--ui-danger); }
.vf-badge.tone-neutral { background: var(--ui-neutral-soft); color: var(--ui-text-muted); }
.vf-review-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 14px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  color: var(--ui-text);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  white-space: nowrap;
}
.vf-review-btn:hover {
  border-color: var(--ui-accent);
  color: var(--ui-accent-text);
}
.vf-skel {
  display: block;
  height: 38px;
  border-radius: var(--ui-radius-md);
  background: linear-gradient(90deg, var(--ui-surface-sunken), var(--ui-surface-hover), var(--ui-surface-sunken));
  background-size: 200% 100%;
  animation: vf-shimmer 1.2s infinite linear;
}
.vf-skel--block {
  height: 140px;
  margin-bottom: var(--ui-space-4);
}
@keyframes vf-shimmer {
  to { background-position: -200% 0; }
}
.vf-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  color: var(--ui-text-muted);
  text-align: center;
}
.vf-empty i {
  font-size: 36px;
  color: var(--ui-accent-text);
}
.vf-empty p {
  margin: 0;
}
.vf-pager {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--ui-space-3);
  padding: var(--ui-space-3) var(--ui-space-5);
  border-top: 1px solid var(--ui-border);
  font-size: var(--ui-fs-sm);
}
.vf-pager-ctrl {
  display: flex;
  align-items: center;
  gap: 8px;
}
.vf-pager-ctrl select {
  width: auto;
}
.vf-icon-btn {
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  border: 1px solid var(--ui-border);
  border-radius: 50%;
  background: var(--ui-surface);
  color: var(--ui-text);
}
.vf-icon-btn:hover:not(:disabled) {
  border-color: var(--ui-accent);
  color: var(--ui-accent-text);
}
.vf-icon-btn:disabled {
  opacity: 0.4;
}

/* Drawer */
.vf-scrim {
  position: fixed;
  inset: 0;
  z-index: 1040;
  background: rgba(15, 15, 20, 0.4);
}
.vf-drawer {
  position: fixed;
  z-index: 1045;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: min(640px, 100vw);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-pop);
  outline: none;
}
.vf-drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ui-space-3);
  padding: var(--ui-space-5);
  border-bottom: 1px solid var(--ui-border);
}
.vf-drawer-head h2 {
  margin: 0;
  font-size: var(--ui-fs-xl);
  font-weight: var(--ui-fw-black);
}
.vf-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--ui-space-5);
}
.vf-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 20px;
  margin: 0 0 var(--ui-space-5);
  padding: var(--ui-space-4);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
}
.vf-facts dt {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
  text-transform: uppercase;
  letter-spacing: var(--ui-tracking-label);
}
.vf-facts dd {
  margin: 2px 0 0;
  font-weight: var(--ui-fw-semibold);
  word-break: break-word;
}
.vf-facts-wide {
  grid-column: 1 / -1;
}
.vf-doc {
  padding: var(--ui-space-4);
  margin-bottom: var(--ui-space-4);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
}
.vf-doc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: var(--ui-space-3);
}
.vf-doc-head h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.vf-doc-head h3 i {
  color: var(--ui-accent-text);
  font-size: 20px;
}
.vf-doc-none {
  margin: 0;
}
.vf-files {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--ui-space-3);
}
.vf-file {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
  text-decoration: none;
  transition: border-color var(--ui-dur-fast) var(--ui-ease), box-shadow var(--ui-dur-fast) var(--ui-ease);
}
a.vf-file:hover {
  border-color: var(--ui-accent);
  box-shadow: var(--ui-shadow-card);
}
.vf-file img {
  width: 100%;
  height: 150px;
  object-fit: contain;
  background: #0f0f14;
}
.vf-file-pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 150px;
  color: var(--ui-text-muted);
  font-weight: var(--ui-fw-bold);
}
.vf-file-pdf i {
  font-size: 40px;
  color: var(--ui-danger);
}
.vf-file--missing .vf-file-pdf i {
  color: var(--ui-text-faint);
}
.vf-file-cap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
}
.vf-reason {
  display: flex;
  gap: 8px;
  margin: var(--ui-space-3) 0 0;
  padding: 10px 12px;
  border-radius: var(--ui-radius-md);
  background: var(--ui-danger-soft);
  color: var(--ui-danger-strong);
  font-size: var(--ui-fs-sm);
}
.vf-decide {
  margin-top: var(--ui-space-4);
  padding-top: var(--ui-space-4);
  border-top: 1px dashed var(--ui-border);
}
.vf-decide-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
.vf-decide textarea {
  margin-bottom: var(--ui-space-3);
}
.vf-label {
  display: block;
  margin-bottom: 6px;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.vf-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.vf-chip {
  padding: 4px 10px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-xs);
}
.vf-chip:hover {
  border-color: var(--ui-accent);
  color: var(--ui-accent-text);
}
.vf-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: var(--ui-space-3);
  padding: 0;
  border: 0;
  background: none;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}

.vf-fade-enter-active,
.vf-fade-leave-active {
  transition: opacity var(--ui-dur) var(--ui-ease);
}
.vf-fade-enter-from,
.vf-fade-leave-to {
  opacity: 0;
}
.vf-slide-enter-active,
.vf-slide-leave-active {
  transition: transform var(--ui-dur) var(--ui-ease);
}
.vf-slide-enter-from,
.vf-slide-leave-to {
  transform: translateX(100%);
}
@media (prefers-reduced-motion: reduce) {
  .vf-slide-enter-active,
  .vf-slide-leave-active,
  .vf-fade-enter-active,
  .vf-fade-leave-active {
    transition: none;
  }
  .vf-skel {
    animation: none;
  }
}

/* Tablet: queues on two rows */
@media (max-width: 991.98px) {
  .vf-queues {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
/* Phones: the table becomes a list of cards */
@media (max-width: 767.98px) {
  .vf-list-head {
    padding: var(--ui-space-4);
  }
  .vf-search {
    width: 100%;
  }
  .vf-table thead {
    display: none;
  }
  .vf-table,
  .vf-table tbody,
  .vf-table tr,
  .vf-table td {
    display: block;
    width: 100%;
  }
  .vf-table tbody tr {
    padding: var(--ui-space-3) var(--ui-space-4);
    border-top: 1px solid var(--ui-border);
  }
  .vf-table td {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0;
    border: 0;
    text-align: right;
  }
  .vf-table td[data-label]::before {
    content: attr(data-label);
    color: var(--ui-text-muted);
    font-size: var(--ui-fs-micro);
    font-weight: var(--ui-fw-bold);
    text-transform: uppercase;
    text-align: left;
  }
  .vf-table td[data-label="Client"] {
    text-align: left;
  }
  .vf-table td[data-label="Client"]::before,
  .vf-table td[data-label="Contact"] {
    display: none;
  }
  .vf-cell-action {
    justify-content: stretch !important;
  }
  .vf-review-btn {
    width: 100%;
    justify-content: center;
  }
  .vf-facts {
    grid-template-columns: 1fr;
  }
  .vf-drawer-head,
  .vf-drawer-body {
    padding: var(--ui-space-4);
  }
}
</style>
